// After mark-in, if the eligible user has no active session AND has pending
// work units, force them to pick a work unit to start a session. Mirrors the
// legacy `TimeManagementContext` flow (see ATTENDANCE REGULARISATION client)
// where `showWorkUnitPopup` was triggered post-mark-in.
//
// If a previous session already exists (paused or working), the server returns
// it via /me/session-state and NavbarTimerPill resumes the timer automatically
// — no gate is shown in that case.

import { useAuthStore } from "@/modules/auth";
import {
  useMeSessionState,
  usePendingWorkUnits,
  useStartSession,
  type PendingWorkUnitRow,
} from "@/api/hooks/timetracking";
import { useConfirm } from "@/shared/components/ConfirmDialog";
import { toast } from "sonner";
import { toastApiError } from "@/api/client";
import { WorkUnitPickerDialog } from "./WorkUnitPickerDialog";

export function SessionStartGate() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const eligible =
    role === "admin" || role === "operations" || role === "employee";

  const meState = useMeSessionState(eligible);
  const attendance = meState.data?.attendance ?? null;
  const session = meState.data?.session ?? null;
  const markedIn = !!attendance?.markedInAt && !attendance?.markedOutAt;
  const sessionLive = !!session && session.state !== "ended";

  // Pending WUs only fetched when the gate would otherwise open — avoids
  // an unnecessary request for users without pending work.
  const needGate = eligible && markedIn && !sessionLive;
  const pending = usePendingWorkUnits(needGate);
  const pendingRows = pending.data ?? [];

  const start = useStartSession();
  const { confirm, node } = useConfirm();

  const open = needGate && pendingRows.length > 0;

  const onPick = async (wu: PendingWorkUnitRow) => {
    const ok = await confirm({
      title: "Start session?",
      message: (
        <>
          Start working on <strong className="mono">{wu.ctrlNo}</strong>.
        </>
      ),
      confirmLabel: "Start",
    });
    if (!ok) return;
    start.mutate(
      { workUnitId: wu.workUnitId },
      {
        onSuccess: () => toast.success(`Started ${wu.ctrlNo}`),
        onError: (e: unknown) => toastApiError(e, "Start failed"),
      },
    );
  };

  if (!open) return node ?? null;

  return (
    <>
      <WorkUnitPickerDialog
        open={open}
        onClose={() => {}}
        onPick={onPick}
        title="Pick a work unit to start your session"
        subtitle="Attendance is marked. Required to continue. Cannot be dismissed."
        forced
      />
      {node}
    </>
  );
}
